import type { BridgeModalProps, BridgeState } from './type'

import { defineStore } from 'pinia'
import { ccipConfig, config } from '@/store/contract'
import { formatTime } from '@/utils/format'
import { CCIPHelper } from '@/utils/sdk/src/CCIPHelper'
import { useAccountStore } from './account'
import { isL2ChainId } from './contract'
import { useStakeStore } from './stake'

export const useBridgeStore = defineStore('bridge', {
  state: (): BridgeState => ({
    allowanceList: {},
    bridgeModal: {
      visible: false,
      amount: '',
      status: 1, // 1 default ; 2 success ; 3 failed
      error: '',
    },
    bridgeData: {
      l1: '',
      l2: '',
    },
  }),
  getters: {},
  actions: {
    changeBridgeModal(bridgeModal: BridgeModalProps) {
      this.bridgeModal = bridgeModal
    },
    cleanBridgeModal() {
      this.bridgeModal = {
        visible: false,
        amount: '',
        status: 1, // 1 default ; 2 success ; 3 failed
        error: '',
      }
    },
    async getAllowance() {
      const accountStore = useAccountStore()
      const chain = isL2ChainId(accountStore.currentNetworkChainId) ? 'L2' : 'L1'
      const tokenAddress = config[chain]?.contracts.WstASTR
      const contractHelper = accountStore.contractHelper
      const allowance = await contractHelper?.checkAllowance(
        tokenAddress,
        accountStore.account as string,
      )
      this.allowanceList[chain] = allowance || '0'
    },
    async getBridgeData() {
      const accountStore = useAccountStore()
      const contractHelper = accountStore.contractHelper
      const bridgeData = await contractHelper?.getBridgeData(accountStore.account as string)
      if (bridgeData) {
        this.bridgeData = bridgeData
      }
    },
    async approveToken(amount: string) {
      const accountStore = useAccountStore()
      const contractHelper = accountStore.contractHelper
      const chain = isL2ChainId(accountStore.currentNetworkChainId) ? 'L2' : 'L1'
      const tokenAddress = config[chain]?.contracts.WstASTR
      const transactionReceipt = await contractHelper?.approveToken(tokenAddress, amount)
      return transactionReceipt
    },

    async checkBridgeAmount(amount: string) {
      const accountStore = useAccountStore()
      const account = accountStore.account as string
      const contractHelper = accountStore.contractHelper
      return await contractHelper?.checkBridgeAmount(account, amount)
    },
    async handleBridgeSend(amount: string) {
      const accountStore = useAccountStore()
      const account = accountStore.account as string
      const stakeStore = useStakeStore()
      const contractHelper = accountStore.contractHelper

      try {
        const result = await contractHelper?.bridgeSend(account, amount)
        if (result.transactionHash) {
          this.bridgeModal.status = 2
          const messageId = await contractHelper?.getMessageId(result.transactionHash)
          let exploreUrl = `https://ccip.chain.link/address/${account}`
          if (messageId) {
            exploreUrl = `${exploreUrl}#/side-drawer/msg/${messageId}`
          }
          const txData = {
            hash: result.transactionHash,
            type: 'bridge',
            exploreUrl,
            amount,
            chainId: accountStore.currentNetworkChainId,
            createdAt: formatTime(new Date().getTime()),
            messageId,
            account,
          }

          stakeStore.changeTxData(txData)
          stakeStore.addTxList(txData)
          return txData
        }
        else {
          this.bridgeModal.status = 3
          this.bridgeModal.error = result.error
        }
      }
      catch (error) {
        this.bridgeModal.status = 3
        this.bridgeModal.error = error instanceof Error ? error.message : String(error)
      }
    },
    // !!!startTimestamp 为开始时间戳，如果超过1小时，直接返回 { state: 'SUCCESS' }
    async getTransferStatus(messageId: string, startTimestamp?: number) {
      try {
        if (startTimestamp && (Date.now() / 1000 - startTimestamp > 3600)) {
          return { state: 'SUCCESS' }
        }
        const ccipHelper = new CCIPHelper(ccipConfig)
        const result = await ccipHelper.getTransferStatus(messageId, 'L2', 20000)
        return result
      }
      catch (error) {
        console.error('Error getTransferStatus:', error)
        throw error
      }
    },

  },
})
